import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { getCurrentUser, getUserType, isAuthenticated, hasPermission } from '../services/authService';
import CargarEnsayos from './CargarEnsayos';
import AnalisisPdfModal from './AnalisisPdfModal';
import { ActiveTab } from '../types';
import {
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
  EyeIcon,
  TrashIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { Button, Card, FormField, IconButton, Input, LoadingState, EmptyState, Modal, Select } from './ui';
import { formatFecha } from '../utils/fecha';
import { getLaboratorios, Laboratorio } from '../services/laboratorioService';

interface AnalisisPendiente {
  id: number;
  id_a_nea: string;
  fecha_solicitud: string;
  rzn_origen: string;
  rzn_lab: string;
  estado: number;
  /** 0 cuando el cliente pidió el análisis y falta asignarle laboratorio. */
  id_lab?: number;
}

interface UserInfo {
  id: number;
  nombre?: string;
  razon_social?: string;
}

const AnalisisPendientes: React.FC = () => {
  const [analisisPendientes, setAnalisisPendientes] = useState<AnalisisPendiente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [selectedAnalisisId, setSelectedAnalisisId] = useState<number | null>(null);
  const [permisos, setPermisos] = useState({
    cargar_analisis: true,
    eliminar_solicitud: true
  });
  const [userType, setUserType] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mostrarComponenteCargarEnsayos, setMostrarComponenteCargarEnsayos] = useState(false);
  const [selectedAnalisisIdString, setSelectedAnalisisIdString] = useState<string>('');
  
  // Estados para el modal de previsualización PDF
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdfAnalisisId, setSelectedPdfAnalisisId] = useState<number | null>(null);

  // Un cliente pide el análisis sin elegir laboratorio: lo asigna después un
  // administrador, y hasta entonces no se pueden cargar resultados.
  const [laboratorios, setLaboratorios] = useState<Laboratorio[]>([]);
  const [analisisAAsignar, setAnalisisAAsignar] = useState<AnalisisPendiente | null>(null);
  const [laboratorioElegido, setLaboratorioElegido] = useState<number | ''>('');
  // Número con el que el laboratorio identifica el análisis en su propio sistema.
  const [numeroDeLaboratorio, setNumeroDeLaboratorio] = useState('');
  const [asignando, setAsignando] = useState(false);
  const [errorAsignacion, setErrorAsignacion] = useState<string | null>(null);

  // Verificar autenticación y cargar datos
  useEffect(() => {
    // Primero verificar si el usuario está autenticado
    if (!isAuthenticated()) {
      setError('No está autenticado. Por favor, inicie sesión nuevamente.');
      setCargando(false);
      // Opcionalmente, redirigir al login
      // window.location.href = '/login';
      return;
    }
    
    cargarAnalisisPendientes();
  }, []);

  const cargarAnalisisPendientes = async () => {
    setCargando(true);
    setError(null);
    
    try {
      // Verificar nuevamente autenticación
      if (!isAuthenticated()) {
        setError('La sesión ha caducado. Por favor, inicie sesión nuevamente.');
        setCargando(false);
        return;
      }
      
      // 1. Obtener información del usuario actual
      const currentUser = getCurrentUser();
      if (!currentUser) {
        setError('No se pudo obtener información del usuario.');
        setCargando(false);
        return;
      }
      
      // 2. Determinar el tipo de usuario
      const type = getUserType();
      setUserType(type);
      
      // 3. Configurar permisos según el tipo de usuario
      if (type === 'cliente') {
        // Los clientes solo pueden previsualizar, no cargar ni eliminar
        setPermisos({
          cargar_analisis: false,
          eliminar_solicitud: false
        });
      } else if (type === 'laboratorio') {
        // Los laboratorios pueden cargar pero no eliminar
        setPermisos({
          cargar_analisis: true,
          eliminar_solicitud: false
        });
      } else if (type === 'administrador') {
        // Los administradores pueden cargar y eliminar solo si tienen permisos específicos
        setPermisos({
          cargar_analisis: true,
          eliminar_solicitud: hasPermission('p_eliminar') // Permiso para eliminar análisis
        });
      }
      
      // 4. Guardar ID del usuario o entidad según su tipo
      if (type === 'cliente' || type === 'laboratorio') {
        setUserInfo({
          id: currentUser.id_grupo || currentUser.id,
          nombre: currentUser.nombre,
          razon_social: currentUser.info_adicional?.razon_social
        });
      }
      
      // 4. Realizar la llamada a la API para obtener análisis pendientes
      // Primero intentar con getAllAnalisis
      try {
        console.log('Intentando cargar análisis pendientes...');
        const response = await api.get('/analisis');
        console.log('Respuesta recibida:', response.data);
        
        // Filtrar los análisis según corresponda
        let pendientes = Array.isArray(response.data) ? response.data : [];
        
        // Filtrar por estado
        pendientes = pendientes.filter((analisis: any) => analisis.estado === 0);
        
        // Filtrar por tipo de usuario si es necesario
        if (type === 'cliente') {
          pendientes = pendientes.filter((analisis: any) => 
            analisis.id_origen === (currentUser.id_grupo || currentUser.id)
          );
        } else if (type === 'laboratorio') {
          pendientes = pendientes.filter((analisis: any) => 
            analisis.id_lab === (currentUser.id_grupo || currentUser.id)
          );
        }
        
        setAnalisisPendientes(pendientes);
      } catch (apiError: any) {
        console.error('Error en la primera solicitud, intentando con ruta alternativa:', apiError);
        
        // Si falla, intentar con la ruta de búsqueda con parámetros
        if (apiError.response && apiError.response.status === 401) {
          throw new Error('Error de autenticación. Por favor, inicie sesión nuevamente.');
        }
        
        let apiUrl = '/analisis';
        let params: Record<string, any> = { estado: 0 };
        
        if (type === 'cliente') {
          params.id_origen = currentUser.id_grupo || currentUser.id;
        } else if (type === 'laboratorio') {
          params.id_lab = currentUser.id_grupo || currentUser.id;
        }
        
        console.log('Intentando ruta alternativa con parámetros:', params);
        const response = await api.get(apiUrl, { params });
        console.log('Respuesta de ruta alternativa:', response.data);
        
        const pendientes = Array.isArray(response.data) 
          ? response.data.filter((analisis: any) => analisis.estado === 0)
          : [];
        
        setAnalisisPendientes(pendientes);
      }
    } catch (error: any) {
      console.error('Error al cargar análisis pendientes:', error);
      
      // Manejar errores específicos
      if (error.response) {
        // Error de respuesta del servidor
        if (error.response.status === 401) {
          setError('La sesión ha caducado. Por favor, inicie sesión nuevamente.');
          // Limpiar datos de sesión
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // window.location.href = '/login';
        } else {
          setError(`Error al cargar los datos (${error.response.status}): ${error.response.data.message || 'Error del servidor'}`);
        }
      } else if (error.request) {
        // No se recibió respuesta del servidor
        setError('No se pudo conectar con el servidor. Verifique su conexión a internet.');
      } else {
        // Error general
        setError(`Error al cargar los datos: ${error.message}`);
      }
    } finally {
      setCargando(false);
    }
  };

  // Función para cargar ensayos de un análisis
  const handleCargar = (id: number) => {
    // Verificar primero si el análisis existe
    const verificarAnalisis = async () => {
      try {
        // Comprobar que podemos acceder al análisis antes de mostrar la pantalla de carga
        await api.get(`/analisis/${id}`);
        
        // Si la petición es exitosa, mostrar el componente de carga de ensayos
        setSelectedAnalisisIdString(id.toString());
        setMostrarComponenteCargarEnsayos(true);
      } catch (error) {
        console.error('Error al verificar el análisis:', error);
        alert('No se puede cargar este análisis. Es posible que no exista o que haya ocurrido un error en el servidor.');
      }
    };
    
    verificarAnalisis();
  };

  const abrirAsignacion = async (analisis: AnalisisPendiente) => {
    setErrorAsignacion(null);
    setLaboratorioElegido('');
    setNumeroDeLaboratorio('');
    setAnalisisAAsignar(analisis);
    if (laboratorios.length === 0) {
      try {
        setLaboratorios(await getLaboratorios());
      } catch (err) {
        console.error('Error al cargar laboratorios:', err);
        setErrorAsignacion('No se pudo cargar la lista de laboratorios.');
      }
    }
  };

  const asignarLaboratorio = async () => {
    if (!analisisAAsignar || laboratorioElegido === '') return;
    setAsignando(true);
    setErrorAsignacion(null);
    try {
      await api.put(`/analisis/${analisisAAsignar.id}`, {
        id_lab: Number(laboratorioElegido),
        id_a_lab: numeroDeLaboratorio.trim(),
      });
      setAnalisisAAsignar(null);
      await cargarAnalisisPendientes();
    } catch (err: any) {
      setErrorAsignacion(err?.response?.data?.message || 'No se pudo asignar el laboratorio.');
    } finally {
      setAsignando(false);
    }
  };

  // Función para previsualizar un análisis
  const handlePrevisualizar = (id: number) => {
    setSelectedPdfAnalisisId(id);
    setPdfModalOpen(true);
  };

  // Función para eliminar un análisis
  const handleEliminar = (id: number) => {
    // Confirmar y eliminar
    if (window.confirm('¿Está seguro que desea eliminar esta solicitud de análisis?')) {
      const eliminarAnalisis = async () => {
        try {
          await api.delete(`/analisis/${id}`);
          // Actualizar la lista después de eliminar
          setAnalisisPendientes(prevState => prevState.filter(analisis => analisis.id !== id));
        } catch (error) {
          console.error('Error al eliminar análisis:', error);
          alert('Error al eliminar el análisis. Inténtelo de nuevo más tarde.');
        }
      };
      
      eliminarAnalisis();
    }
  };

  // Función para volver a la lista de análisis pendientes
  const handleVolverDesdeCargaEnsayos = (tab?: ActiveTab) => {
    setMostrarComponenteCargarEnsayos(false);
    cargarAnalisisPendientes(); // Recargar la lista para reflejar cualquier cambio
  };

  // Si se está mostrando el componente de cargar ensayos, renderizar CargarEnsayos en lugar del listado
  if (mostrarComponenteCargarEnsayos && selectedAnalisisIdString) {
    return (
      <CargarEnsayos 
        analisisId={selectedAnalisisIdString} 
        onNavigate={handleVolverDesdeCargaEnsayos} 
      />
    );
  }

  return (
    <div className="w-full">
      <Card className="mb-6" padded={false}>
        <div className="p-4 bg-brand-50 border-b border-brand-100 text-center">
          <h2 className="font-bold text-xl text-brand-800">Análisis pendientes</h2>
          {userType && userType !== 'administrador' && (
            <p className="text-sm text-brand-600 mt-1">
              Mostrando análisis pendientes {userType === 'cliente' ? 'de su empresa' : 'de su laboratorio'}
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 m-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {cargando ? (
          <LoadingState label="Cargando análisis pendientes..." />
        ) : analisisPendientes.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Análisis n°</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Fecha de generación</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Origen</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Destino</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Opciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {analisisPendientes.map((analisis, index) => (
                  <tr key={analisis.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {analisis.id_a_nea}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {formatFecha(analisis.fecha_solicitud) || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {analisis.rzn_origen}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {Number(analisis.id_lab) > 0
                        ? analisis.rzn_lab
                        : <span className="text-amber-700">Sin asignar</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      <div className="flex space-x-1">
                        {/* Asignar laboratorio: sólo el administrador, y sólo
                            mientras el análisis no tenga uno. */}
                        {userType === 'administrador' && !(Number(analisis.id_lab) > 0) && (
                          <IconButton
                            onClick={() => abrirAsignacion(analisis)}
                            label="Asignar laboratorio"
                            tone="brand"
                            icon={<BuildingOffice2Icon className="w-5 h-5" />}
                          />
                        )}

                        {/* Botón de cargar ensayos - Solo para laboratorios y administradores */}
                        {permisos.cargar_analisis && Number(analisis.id_lab) > 0 && (
                          <IconButton
                            onClick={() => handleCargar(analisis.id)}
                            label="Cargar ensayos"
                            tone="brand"
                            icon={<ArrowUpTrayIcon className="w-5 h-5" />}
                          />
                        )}

                        {/* Vista previa: en un análisis pendiente lista los
                            analitos de cada muestra, sin resultados ni impresión. */}
                        <IconButton
                          onClick={() => handlePrevisualizar(analisis.id)}
                          label="Previsualizar"
                          tone="brand"
                          icon={<EyeIcon className="w-5 h-5" />}
                        />

                        {/* Botón de eliminar - Solo para administradores con permiso */}
                        {permisos.eliminar_solicitud && (
                          <IconButton
                            onClick={() => handleEliminar(analisis.id)}
                            label="Eliminar"
                            tone="danger"
                            icon={<TrashIcon className="w-5 h-5" />}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No hay análisis pendientes." />
        )}
      </Card>

      {/* Asignación de laboratorio */}
      {analisisAAsignar && (
        <Modal
          title={`Asignar laboratorio — Análisis ${analisisAAsignar.id_a_nea}`}
          onClose={() => setAnalisisAAsignar(null)}
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAnalisisAAsignar(null)} disabled={asignando}>
                Cancelar
              </Button>
              <Button onClick={asignarLaboratorio} loading={asignando} disabled={laboratorioElegido === ''}>
                Asignar
              </Button>
            </div>
          }
        >
          <p className="mb-4 text-sm text-slate-600">
            El cliente generó esta solicitud sin laboratorio. Elija a cuál se envía para que pueda cargar
            los resultados.
          </p>
          {errorAsignacion && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {errorAsignacion}
            </div>
          )}
          <FormField label="Laboratorio" htmlFor="laboratorio-asignado" required>
            <Select
              id="laboratorio-asignado"
              value={laboratorioElegido}
              onChange={e => setLaboratorioElegido(e.target.value === '' ? '' : Number(e.target.value))}
              required
            >
              <option value="">Seleccionar laboratorio</option>
              {laboratorios.map(lab => (
                <option key={lab.id} value={lab.id}>{lab.nombre}</option>
              ))}
            </Select>
          </FormField>

          <FormField label="Número de análisis de laboratorio" htmlFor="numero-laboratorio" className="mt-4">
            <Input
              id="numero-laboratorio"
              type="text"
              value={numeroDeLaboratorio}
              onChange={e => setNumeroDeLaboratorio(e.target.value)}
              placeholder="Opcional"
            />
            <span className="mt-1 block text-xs text-slate-500">
              El número con el que el laboratorio identifica este análisis en su propio sistema.
            </span>
          </FormField>
        </Modal>
      )}

      {/* Modal de previsualización PDF */}
      {pdfModalOpen && selectedPdfAnalisisId && (
        <AnalisisPdfModal
          isOpen={pdfModalOpen}
          onClose={() => {
            setPdfModalOpen(false);
            setSelectedPdfAnalisisId(null);
          }}
          analisisId={selectedPdfAnalisisId}
        />
      )}
    </div>
  );
};

export default AnalisisPendientes; 